import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { ShieldCheck, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import { useRolesPermissionsPageData } from "../hooks";
import CreateRoleDialog from "../components/CreateRoleDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import RolesPermissionsTable from "../components/RolesPermissionsTable";

const RolesPermissionsPage = observer(() => {
  const { t } = useTran();
  const {
    roles,
    selectedRole,
    dialogOpen,
    dialogMode,
    deleteDialogOpen,
    loadingRoles,
    creatingRole,
    updatingRole,
    deletingRoleId,
    openCreateDialog,
    openEditDialog,
    openDeleteDialog,
    closeDeleteDialog,
    closeDialog,
    setDialogOpen,
    isAdminGroup,
    companyId,
    filteredModules,
    visibleRoleItems,
    roleUsageById,
    roleUsageLoading,
    handleSaveRole,
    handleConfirmDelete,
  } = useRolesPermissionsPageData();

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("Roles & Permissions")}
        description={t("Review which capabilities are granted to each role.")}
        icon={ShieldCheck}
        actions={
          !isAdminGroup ? (
            <Button
              type="button"
              size="sm"
              className="gap-2"
              onClick={openCreateDialog}
            >
              <Plus className="h-4 w-4" />
              {t("Add New Role")}
            </Button>
          ) : null
        }
      />

      <div className="overflow-hidden rounded-radius border border-border bg-card p-4">
        <RolesPermissionsTable
          data={visibleRoleItems}
          loading={loadingRoles}
          isAdminGroup={isAdminGroup}
          companyId={companyId}
          roleUsageById={roleUsageById}
          roleUsageLoading={roleUsageLoading}
          onEdit={openEditDialog}
          onDelete={openDeleteDialog}
        />
      </div>

      <CreateRoleDialog
        open={dialogOpen}
        onOpenChange={(openState) => {
          if (openState) {
            setDialogOpen(true);
          } else {
            closeDialog();
          }
        }}
        modules={filteredModules}
        onSave={handleSaveRole}
        mode={dialogMode}
        role={selectedRole}
        saving={dialogMode === "edit" ? updatingRole : creatingRole}
        existingRoles={roles}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(openState) => {
          if (!openState) {
            closeDeleteDialog();
          }
        }}
        onConfirm={handleConfirmDelete}
        title={t("Delete role")}
        description={t(
          "Are you sure you want to delete this role? This action cannot be undone.",
        )}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        tone="danger"
        loading={Boolean(deletingRoleId)}
      />
    </div>
  );
});

export default RolesPermissionsPage;
